import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Table, TablePager, useTable } from "@netapp/bxp-design-system-react";
import DOMPurify from "dompurify";
import "../detail.scss"; // Ensure this is imported after any other CSS files

const linkifyWorkarounds = (html) => {
  const content = DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true });
  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    if (!walker.currentNode.parentElement?.closest("a")) {
      textNodes.push(walker.currentNode);
    }
  }

  textNodes.forEach((textNode) => {
    const urlPattern = /https?:\/\/[^\s<>"']+/gi;
    const replacement = document.createDocumentFragment();
    let position = 0;
    for (const match of textNode.textContent.matchAll(urlPattern)) {
      let url = match[0].replace(/[.,;:!?]+$/, "");
      while (url.endsWith(")") && url.split(")").length > url.split("(").length) {
        url = url.slice(0, -1);
      }
      replacement.append(textNode.textContent.slice(position, match.index));
      const link = document.createElement("a");
      link.href = url;
      link.textContent = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      replacement.append(link);
      position = match.index + url.length;
    }
    if (position > 0) {
      replacement.append(textNode.textContent.slice(position));
      textNode.replaceWith(replacement);
    }
  });

  const container = document.createElement("div");
  container.append(content);
  container.querySelectorAll("a[href]").forEach((a) => {
    a.target = "_blank";
    a.rel = "noopener noreferrer";
  });
  return container.innerHTML;
};

const Remediation = ({ advisoryData, loading }) => {
  const { t } = useTranslation();
  const workaroundsHtml = useMemo(
    () => linkifyWorkarounds(advisoryData?.kb_workarounds || ""),
    [advisoryData?.kb_workarounds]
  );

  // Define the columns for the table
  const tableColumns = useMemo(() => {
    return [
      {
        header: t("advisory_single_fixes_product.other"),
        accessor: "product",
        id: "product",
        width: 50,
      },
      {
        header: t("advisory_single_fixes_firstFixed.other"),
        accessor: "firstFixed",
        id: "firstFixed",
        width: 50,
      },
    ];
  }, [t]);

  // Define the rows for the table
  const tableRows = useMemo(() => {
    if (loading || !advisoryData?.kb_fixes) {
      return [];
    }
    console.log(advisoryData.kb_fixes);
    return advisoryData.kb_fixes.map((fix, index) => ({
      product: fix.product,
      firstFixed: (
        <div>
          {fix.fixes.length === 0 && fix.wontfix === true && (
            <div style={{ display: "flex"}} >
            <span style={{fontFamily: "SF-Pro, sans-serif", fontSize: "14px" }}>
                {fix.product} has no plans to address this vulnerability.
                {fix.eos_link && (
                <>
                  {" "}
                  See the{" "}
                  <a
                  href={fix.eos_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  >
                  Support Announcement
                  </a>{" "}
                  for more information.{" "}
                </>
                )}
            </span> </div>
          )}
          {fix.fixes.length > 0 ? (
            <div>
              {fix.fixes.map((fixItem, fixIndex) => (
                <a
                  key={fixIndex}
                  href={fixItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "flex"}}
                >
                  {fixItem.link}
                </a>
              ))}
              <p className="custom-instruction" dangerouslySetInnerHTML={{ __html: fix.instructions }} />
            </div>
          ) : (
            <p className="custom-instruction" dangerouslySetInnerHTML={{ __html: fix.instructions }} />
          )}
        </div>
      ),
      id: index,
    }));
  }, [loading, advisoryData]);

  const {
    pagination,
    organizedRows,
    sortState,
    toggleSort,
    rowState,
    updateRowState,
  } = useTable({
    columns: tableColumns,
    rows: tableRows,
    pageSize: 10,
    isSorting: true,
  });

  const disclaimerText = t("advisory_single_disclaimer.other").replace('YEAR', new Date().getFullYear());

  return (
    <div className="overview-content">
      <h2>{t("advisory_single_section_fixes.other")}</h2>
      <p>{t("advisory_single_fixes_intro.other")}</p>
      {tableRows.length === 0 ? (
        <>
          <p>{t("advisory_single_fixes_none.other")}</p>
          <p>{t("advisory_single_fixes_willBeUpdated.other")}</p>
        </>
      ) : (
        <>
          <div className="table-pagination-container">
            <div className="custom-table-container">
              <Table
                columns={tableColumns}
                rows={pagination.pageRows}
                sortState={sortState}
                toggleSort={toggleSort}
                updateRowState={updateRowState}
                rowState={rowState}
                style={{ tableLayout: "auto" }}
                rowClassName="custom-table-row"
                cellClassName="custom-table-cell"
                rowStyle={{ height: "auto" }}
                cellStyle={{
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  padding: "10px",
                }}
              />
            </div>
            <TablePager
              pageRows={pagination.pageRows}
              pageSize={10}
              rows={organizedRows}
              pageIndex={pagination.pageIndex}
              pageCount={pagination.pageCount}
              gotoPage={pagination.gotoPage}
            />
          </div>
        </>
      )}

      <h2>{t("advisory_single_section_workarounds.other")}</h2>
      {advisoryData?.kb_workarounds ? (
        <div dangerouslySetInnerHTML={{ __html: workaroundsHtml }} />
      ) : (
        <p>None at this time.</p>
      )}

      <h2>{t("advisory_single_section_obtainingFixes.other")}</h2>
      <p>{t("advisory_single_nss_intro.other")}</p>
      <p>
        <a
          href="https://mysupport.netapp.com/site/downloads/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://mysupport.netapp.com/site/downloads/
        </a>
      </p>
      <p>{t("advisory_single_nss_callSupport.other")}</p>

      <h2>{t("advisory_single_section_contactInfo.other")}</h2>
      <p>
        Check{" "}
        <a
          href="http://mysupport.netapp.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          http://mysupport.netapp.com
        </a>{" "}
        for further updates. For questions, contact NetApp at:
      </p>

      <div className="contact-info">
        <p>
          <strong>{t("advisory_single_nss_techSupport.other")}</strong>
        </p>
        <p>
          <a
            href="http://mysupport.netapp.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            mysupport.netapp.com
          </a>
        </p>
        <p>1 888 4 NETAPP (1 888 463 8277) (U.S. and Canada)</p>
        <p>+00 800 44 638277 (EMEA/Europe)</p>
        <p>+800 800 80 800 (Asia/Pacific)</p>
      </div>
      <div className="advisory-note">
        {/* <p>{t("advisory_single_disclaimer.other")}</p> */}
        <p>{disclaimerText}</p>
      </div>
    </div>
  );
};

export default Remediation;
